import React, { useState, useEffect, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { supabase } from '../supabaseClient';
import { PROFILE_SELF_COLUMNS } from '../lib/profileColumns';
import {
  WalletCards, DollarSign, ArrowDownCircle, ArrowUpCircle,
  CreditCard, CheckCircle, Clock, Lock, Zap,
  ChevronRight, Building, Plus
} from 'lucide-react';
import BottomSheet from './shared/BottomSheet';
import Toast from './shared/Toast';
import EmptyState from './shared/EmptyState';
import AdvisoryBanner from './shared/AdvisoryBanner';
import { RowSkeleton } from './shared/Skeleton';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Which countries fetchr can pay out to is NOT ours to hardcode — Stripe
// restricts which countries a platform may create connected accounts in,
// and a wrong guess fails at account-creation time ("Connected accounts in
// CH cannot be created by platforms in AE"). The list comes from the
// payout_countries action (Stripe's own country_specs). Names come from
// the browser rather than a map we'd have to maintain; if Intl.DisplayNames
// isn't available we just show the ISO code, which is still selectable.
let regionNames = null;
try {
  regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
} catch (e) {
  regionNames = null;
}
const countryName = (code) => {
  try {
    return regionNames?.of(code) || code;
  } catch (e) {
    return code;
  }
};

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '14px',
      fontFamily: '"IBM Plex Mono", monospace',
      color: '#14181F',
      '::placeholder': { color: '#7F8794' },
      iconColor: '#14181F',
    },
    invalid: { color: '#B0301C', iconColor: '#B0301C' },
  },
  hidePostalCode: true,
};

const callStripe = async (action, data) => {
  const { data: { session } } = await supabase.auth.getSession();
  const res = await fetch(
    'https://jvuzjmigkqolphkhzeei.supabase.co/functions/v1/stripe-connect',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ action, data }),
    }
  );
  const result = await res.json();
  if (!res.ok || result.error) throw new Error(result.error || 'Request failed');
  return result;
};

// ── TOP UP FORM ──
// User selects saved card (enters CVC) OR enters a completely new card
// Stripe CardElement handles all card input — number never touches fetchr servers
const TopUpForm = ({ profile, onSuccess, onClose }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [amount, setAmount] = useState('');
  const [useNewCard, setUseNewCard] = useState(false);
  const [cardReady, setCardReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState('form');

  const hasSavedCard = !!(profile?.stripe_payment_method_id);
  const savedCardLabel = hasSavedCard
    ? `${profile.payout_card_brand
        ? profile.payout_card_brand.charAt(0).toUpperCase() + profile.payout_card_brand.slice(1)
        : 'Card'} ****${profile.payout_card_last4}`
    : null;

  // showNewCardForm is true when no saved card, or user explicitly chose new card
  const showNewCardForm = !hasSavedCard || useNewCard;

  const handlePay = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { setError('Enter a valid amount greater than zero.'); return; }
    if (!stripe || !elements) { setError('Stripe not loaded. Please wait.'); return; }

    // For new card flow, card must be ready
    if (showNewCardForm && !cardReady) {
      setError('Card form still loading. Please wait a moment.'); return;
    }

    setError(''); setLoading(true); setStep('processing');

    try {
      if (showNewCardForm) {
        // ── NEW CARD FLOW ──
        // CardElement is always mounted (just hidden when not shown)
        // so getElement() always returns a valid reference
        const cardElement = elements.getElement(CardElement);
        if (!cardElement) throw new Error('Card form not available. Please refresh and try again.');

        const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
        });
        if (pmError) throw new Error(pmError.message);

        const result = await callStripe('top_up_wallet', {
          amount: amt,
          paymentMethodId: paymentMethod.id,
        });

        if (result.requiresAction) {
          const { error: actionError, paymentIntent } = await stripe.handleNextAction({
            clientSecret: result.clientSecret,
          });
          if (actionError) throw new Error(actionError.message);
          if (paymentIntent?.status === 'succeeded') {
            await callStripe('confirm_top_up', {
              paymentIntentId: result.paymentIntentId,
              amount: amt,
            });
          } else {
            throw new Error('Authentication incomplete. Please try again.');
          }
        }

      } else {
        // ── SAVED CARD FLOW ──
        const result = await callStripe('create_topup_intent', {
          amount: amt,
          paymentMethodId: profile.stripe_payment_method_id,
        });

        // stripe.confirmCardPayment handles 3DS popup automatically
        const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
          result.clientSecret,
          { payment_method: profile.stripe_payment_method_id }
        );
        if (confirmError) throw new Error(confirmError.message);

        if (paymentIntent.status === 'succeeded') {
          await callStripe('confirm_top_up', {
            paymentIntentId: paymentIntent.id,
            amount: amt,
          });
        } else {
          throw new Error(`Payment not completed (${paymentIntent.status}). Please try again.`);
        }
      }

      setStep('success');
      setTimeout(() => onSuccess(amt), 1500);

    } catch (e) {
      setError(e.message);
      setStep('form');
    }
    setLoading(false);
  };

  if (step === 'processing') return (
    <div className="p-8 text-center">
      <div className="w-16 h-16 bg-ink-100 rounded-lg flex items-center justify-center mx-auto mb-4">
        <div className="w-8 h-8 border-2 border-ink-900 border-t-transparent rounded-full animate-spin" />
      </div>
      <p className="font-display font-bold text-ink-900 mb-1">Processing payment</p>
      <p className="text-body-s text-content-muted">Charging your card via Stripe</p>
      <p className="text-micro text-content-subtle mt-2">
        If your bank requires it, a verification screen will appear automatically.
      </p>
    </div>
  );

  if (step === 'success') return (
    <div className="p-8 text-center">
      <div className="w-16 h-16 bg-success-tint rounded-lg flex items-center justify-center mx-auto mb-4">
        <CheckCircle size={32} className="text-success" />
      </div>
      <p className="font-display font-bold text-ink-900 mb-1">Wallet topped up.</p>
      <p className="text-body-s text-content-muted">
        ${parseFloat(amount).toFixed(2)} added to your wallet.
      </p>
    </div>
  );

  return (
    <div className="p-5 space-y-4">
      {/* Amount */}
      <div>
        <label className="block text-label text-content-muted mb-1.5 uppercase">
          Amount (USD)
        </label>
        <div className="relative">
          <DollarSign size={15} className="absolute left-3.5 top-3.5 text-ink-400 pointer-events-none" />
          <input
            type="number"
            placeholder="0.00"
            min="0.01"
            step="0.01"
            autoComplete="off"
            value={amount}
            onChange={e => {
              const v = e.target.value;
              if (v === '' || parseFloat(v) >= 0) setAmount(v);
            }}
            className="input-field pl-9"
          />
        </div>
        <div className="flex gap-2 mt-2">
          {[25, 50, 100, 250].map(a => (
            <button key={a} type="button" onClick={() => setAmount(a.toString())}
              className={`flex-1 py-2 rounded-md text-label font-bold border transition-all ${
                amount === a.toString()
                  ? 'bg-brand text-white border-brand'
                  : 'bg-surface-sunken text-content border-line-strong hover:border-paper-600'
              }`}>
              ${a}
            </button>
          ))}
        </div>
      </div>

      {/* Payment method selector — only shown when a saved card exists */}
      {hasSavedCard && (
        <div className="space-y-2">
          <label className="block text-label text-content-muted uppercase">
            Payment method
          </label>
          {[
            {
              val: false,
              label: savedCardLabel,
              sub: 'Stripe handles 3DS authentication automatically',
              Icon: CreditCard,
            },
            {
              val: true,
              label: 'Use a different card',
              sub: 'Enter new card details',
              Icon: Plus,
            },
          ].map(opt => (
            <button key={String(opt.val)} type="button"
              onClick={() => {
                setUseNewCard(opt.val);
                setError('');
                // Don't reset cardReady — CardElement stays mounted
              }}
              className={`w-full flex items-center gap-3 p-3 rounded-md border-2 transition-all text-left ${
                useNewCard === opt.val
                  ? 'border-ink-900 bg-surface-sunken'
                  : 'border-line hover:border-line-strong'
              }`}>
              <opt.Icon size={18} className="text-ink-600 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-body-m font-semibold text-ink-900">{opt.label}</p>
                <p className="text-micro text-content-subtle">{opt.sub}</p>
              </div>
              {useNewCard === opt.val && (
                <CheckCircle size={16} className="text-ink-900 flex-shrink-0" />
              )}
            </button>
          ))}
        </div>
      )}

      {/* CardElement — ALWAYS mounted in DOM to prevent unmount/remount issues */}
      {/* Shown when: no saved card, OR user chose "use a different card" */}
      {/* Hidden (but mounted) otherwise — this prevents the "element not ready" error */}
      <div style={{ display: showNewCardForm ? 'block' : 'none' }}>
        <label className="block text-label text-content-muted mb-1.5 uppercase">
          Card details
        </label>
        <div
          className="border border-line-strong rounded-md px-4 py-3.5 focus-within:border-signal-500 transition-all bg-surface"
          data-lpignore="true"
          data-form-type="other"
        >
          <CardElement
            options={{
              style: CARD_ELEMENT_OPTIONS.style,
              hidePostalCode: true,
              // Disable Link wallet feature entirely in the element
              wallets: { link: 'never' },
            }}
            onReady={() => setCardReady(true)}
          />
        </div>
        <p className="text-micro text-content-subtle mt-1.5 flex items-center gap-1">
          <Lock size={10} /> Card details encrypted by Stripe — never stored on fetchr servers
        </p>
        <AdvisoryBanner tone="warning" title="Test mode" className="mt-2">
          Standard: <strong>4242 4242 4242 4242</strong> · Any future date · Any 3-digit CVC<br />
          3DS auth: <strong>4000 0025 0000 3155</strong> · Any future date · Any 3-digit CVC
        </AdvisoryBanner>
      </div>

      {/* Info shown when saved card is selected */}
      {hasSavedCard && !useNewCard && (
        <AdvisoryBanner tone="info">
          Tap Pay to charge your saved card. If your bank requires 3DS verification, a secure popup will appear automatically.
        </AdvisoryBanner>
      )}

      {error && <AdvisoryBanner tone="error">{error}</AdvisoryBanner>}

      <div className="flex gap-2">
        <button onClick={onClose} className="btn-secondary flex-1">Cancel</button>
        <button
          onClick={handlePay}
          disabled={
            loading ||
            !stripe ||
            !amount ||
            parseFloat(amount) <= 0 ||
            (showNewCardForm && !cardReady)
          }
          className="btn-signal flex-[2] disabled:opacity-50">
          <Lock size={15} />
          {loading
            ? 'Processing'
            : `Pay $${parseFloat(amount) > 0 ? parseFloat(amount).toFixed(2) : '0.00'}`
          }
        </button>
      </div>

      <p className="text-micro text-content-subtle text-center flex items-center justify-center gap-1">
        <Lock size={10} /> Secured by Stripe · PCI DSS · Visa/Mastercard 3DS
      </p>
    </div>
  );
};

// ── WITHDRAW FORM ──
// Payouts go through a Stripe Connect Express account rather than a raw
// bank-account form — collecting account/routing numbers directly through
// our own UI is the kind of thing Stripe restricts to platforms with
// special approval; Connect's hosted onboarding does the KYC/bank-linking
// properly and shifts that compliance burden to Stripe instead of us.
// Edge function verifies balance against ALL DB transactions before releasing funds.
const WithdrawForm = ({ profile, forceWithdrawAll, onSuccess, onClose }) => {
  const [amount, setAmount] = useState(
    forceWithdrawAll ? (profile?.wallet_balance || 0).toFixed(2) : ''
  );
  const [loading, setLoading] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState('form');
  const [connectStatus, setConnectStatus] = useState(null); // null = checking
  const [payoutMethod, setPayoutMethod] = useState('standard');
  const [payoutCountry, setPayoutCountry] = useState('');
  const [payoutCountries, setPayoutCountries] = useState(null); // null = loading
  const [result, setResult] = useState(null);
  const WITHDRAWAL_FEE_PCT = 2.5;
  const MIN_WITHDRAWAL = forceWithdrawAll ? 0 : 10;

  const amt = parseFloat(amount) || 0;
  const fee = amt * WITHDRAWAL_FEE_PCT / 100;
  const net = amt - fee;

  // Check live with Stripe, not the (possibly stale) cached profile prop —
  // right after finishing onboarding, the account.updated webhook may not
  // have landed yet.
  useEffect(() => {
    callStripe('connect_account_status').then(res => {
      setConnectStatus(res);
      if (res.bankAccountCountry) setPayoutCountry(res.bankAccountCountry);
      // A card-only account (no bank on file) has no confirmed way to
      // receive Stripe's standard scheduled payout — instant-to-card is
      // the only rail known to actually work for them, so it's not a
      // preference to offer, it's the only functional choice.
      if (res.hasInstantCard && !res.hasBankAccount) setPayoutMethod('instant');
    }).catch(() => setConnectStatus({ connected: false, payoutsEnabled: false }));

    // Asked once per open, not hardcoded — see countryName above.
    callStripe('payout_countries')
      .then(setPayoutCountries)
      .catch(() => setPayoutCountries({ platformCountry: null, supportedCountries: [] }));
  }, []);

  const startBankConnect = async () => {
    if (!payoutCountry) { setError('Select which country your payout account is in first.'); return; }
    setConnecting(true); setError('');
    const isNative = Capacitor.isNativePlatform();
    const pendingTab = isNative ? null : window.open('', '_blank');
    try {
      // Must run before create_connect_account every time — if the user
      // is switching countries after a stalled onboarding attempt, this
      // is also what tears down the stale (wrong-country) Connect account
      // so a fresh one gets created under the corrected one.
      await callStripe('set_payout_country', { countryCode: payoutCountry });
      await callStripe('create_connect_account');
      const returnUrl = window.location.href;
      const { url } = await callStripe('create_connect_onboarding_link', { returnUrl, refreshUrl: returnUrl });
      if (isNative) await Browser.open({ url });
      else if (pendingTab) pendingTab.location.href = url;
      else window.open(url, '_blank');
    } catch (e) {
      setError(e.message); pendingTab?.close();
    }
    setConnecting(false);
  };

  const handleWithdraw = async () => {
    if (!amt || amt <= 0) { setError('Enter a valid amount.'); return; }
    if (amt > (profile?.wallet_balance || 0)) {
      setError(`Insufficient balance. Available: $${(profile?.wallet_balance || 0).toFixed(2)}`); return;
    }
    if (!forceWithdrawAll && amt < MIN_WITHDRAWAL) {
      setError(`Minimum withdrawal is $${MIN_WITHDRAWAL}.`); return;
    }
    if (!connectStatus?.payoutsEnabled) {
      setError('Connect your bank via Stripe before withdrawing.'); return;
    }

    setLoading(true); setError(''); setStep('processing');
    try {
      const res = await callStripe('withdraw_to_bank', { amount: amt, method: payoutMethod });
      setResult(res);
      setStep('success');
      setTimeout(() => onSuccess(res), 1500);
    } catch (e) {
      setError(e.message);
      setStep('form');
    }
    setLoading(false);
  };

  if (step === 'processing') return (
    <div className="p-8 text-center">
      <div className="w-16 h-16 bg-ink-100 rounded-lg flex items-center justify-center mx-auto mb-4">
        <div className="w-8 h-8 border-2 border-ink-900 border-t-transparent rounded-full animate-spin" />
      </div>
      <p className="font-display font-bold text-ink-900 mb-1">Processing withdrawal</p>
      <p className="text-body-s text-content-muted">Verifying balance and submitting payout via Stripe</p>
    </div>
  );

  if (step === 'success') return (
    <div className="p-8 text-center">
      <div className="w-16 h-16 bg-success-tint rounded-lg flex items-center justify-center mx-auto mb-4">
        <CheckCircle size={32} className="text-success" />
      </div>
      <p className="font-display font-bold text-ink-900 mb-1">Withdrawal initiated.</p>
      {result?.estimatedArrival ? (
        <p className="text-body-s text-content-muted">
          ${net.toFixed(2)} will arrive {result.estimatedArrival}
          {result.payoutMethod === 'instant' ? ' — Stripe deducts its own instant-payout fee from this on top of fetchr\'s fee shown above.' : '.'}
        </p>
      ) : (
        <p className="text-body-s text-content-muted">
          ${net.toFixed(2)} has left your fetchr wallet and is now sitting in your connected payout account, ready to go — see below.
        </p>
      )}
      {result?.instantPayoutFailed && (
        result.noFallbackAvailable ? (
          <p className="text-micro text-warning mt-2">
            The instant transfer to your card couldn't complete right now, and there's no bank account on file to fall back to. Your money is safe — it's sitting in your connected payout account. Try the instant withdrawal again shortly.
          </p>
        ) : (
          <p className="text-micro text-warning mt-2">
            Instant transfer couldn't complete right now — this will arrive via the standard 2-5 day payout instead. Your money is safe, nothing was lost.
          </p>
        )
      )}
    </div>
  );

  return (
    <div className="p-5 space-y-4">
      <p className="text-micro text-content-subtle">
        {WITHDRAWAL_FEE_PCT}% fee · {forceWithdrawAll ? 'No minimum (account closure)' : 'Min $10'} · 3-5 business days
      </p>

      {/* Amount */}
      <div>
        <label className="block text-label text-content-muted mb-1.5 uppercase">
          Amount · Available: ${(profile?.wallet_balance || 0).toFixed(2)}
        </label>
        <div className="relative">
          <DollarSign size={15} className="absolute left-3.5 top-3.5 text-ink-400 pointer-events-none" />
          <input type="number" placeholder="0.00" min="0.01"
            max={profile?.wallet_balance || 0} step="0.01" value={amount}
            onChange={e => { const v = e.target.value; if (v === '' || parseFloat(v) >= 0) setAmount(v); }}
            className="input-field pl-9" />
        </div>
        {(profile?.wallet_balance || 0) > 0 && (
          <button type="button"
            onClick={() => setAmount((profile.wallet_balance || 0).toFixed(2))}
            className="mt-1.5 text-body-s text-ink-900 font-semibold hover:underline">
            Withdraw full balance (${(profile?.wallet_balance || 0).toFixed(2)})
          </button>
        )}
        {amt > 0 && (
          <div className="mt-2 bg-surface-sunken rounded-md p-3 text-body-s font-mono space-y-1.5 border border-line">
            <div className="flex justify-between text-content-muted">
              <span>Withdrawal amount</span><span>${amt.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-danger">
              <span>Fee ({WITHDRAWAL_FEE_PCT}%)</span><span>−${fee.toFixed(2)}</span>
            </div>
            <div className="flex justify-between font-bold text-ink-900 border-t border-line pt-1.5">
              <span>You receive</span><span>${net.toFixed(2)}</span>
            </div>
          </div>
        )}
      </div>

      {/* Bank connection status */}
      <div>
        <label className="block text-label text-content-muted mb-1.5 uppercase">
          Payout account
        </label>
        {connectStatus === null ? (
          <div className="h-14 bg-surface-sunken rounded-md animate-pulse" />
        ) : connectStatus.payoutsEnabled ? (
          <div className="flex items-center gap-3 p-3 rounded-md border-2 border-success bg-success-tint">
            <CheckCircle size={18} className="text-success flex-shrink-0" />
            <div className="flex-1">
              <p className="text-body-m font-semibold text-ink-900">Connected via Stripe</p>
              <p className="text-micro text-content-subtle">Payouts go directly to your linked bank account</p>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center gap-3 p-3 rounded-md border-2 border-line">
              <Building size={18} className="text-ink-400 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-body-m font-semibold text-ink-900">
                  {connectStatus.connected ? 'Onboarding not finished yet' : 'No payout account connected'}
                </p>
                <p className="text-micro text-content-subtle">Required before you can withdraw — bank account or debit card</p>
              </div>
            </div>
            {/* Only the countries Stripe will actually let fetchr create a
                payout account in — asked at runtime, never hardcoded.
                Stripe limits cross-border payouts to platforms in the
                US/UK/EEA/CA/CH; fetchr is UAE-registered, so this list is
                whatever AE is actually allowed to reach and nothing more. */}
            <div>
              <label className="block text-label text-content-muted mb-1.5 uppercase">
                Country your payout account is in
              </label>
              {payoutCountries === null ? (
                <div className="h-11 bg-surface-sunken rounded-md animate-pulse" />
              ) : (payoutCountries.supportedCountries || []).length === 0 ? (
                <AdvisoryBanner tone="warning">
                  Payouts aren't available yet. Your balance is safe and stays in your
                  wallet — nothing is lost, and it'll be withdrawable as soon as payouts
                  are switched on.
                </AdvisoryBanner>
              ) : (
                <select value={payoutCountry} onChange={e => setPayoutCountry(e.target.value)}
                  className="input-field">
                  <option value="">Select a country…</option>
                  {[...(payoutCountries.supportedCountries || [])]
                    .map(code => [code, countryName(code)])
                    .sort((a, b) => a[1].localeCompare(b[1]))
                    .map(([code, name]) => <option key={code} value={code}>{name}</option>)}
                </select>
              )}
            </div>
            {(payoutCountries === null || (payoutCountries.supportedCountries || []).length > 0) && (
              <button type="button" onClick={startBankConnect}
                disabled={connecting || !payoutCountry || payoutCountries === null}
                className="w-full btn-primary disabled:opacity-50">
                {connecting ? 'Opening Stripe…' : 'Connect a payout method via Stripe'}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Instant vs standard — a real choice only when BOTH a bank account
          and a debit card are on file. A card-only traveler (no bank
          account at all — common, not an edge case) has no confirmed way
          to receive Stripe's standard scheduled payout, so instant is
          their only functional option, not a preference to offer. Stripe's
          own instant-payout fee comes out on top of fetchr's fee above; we
          don't know that number ahead of time, so we don't pretend to
          quote a final total for it. */}
      {connectStatus?.payoutsEnabled && connectStatus?.hasInstantCard && connectStatus?.hasBankAccount && (
        <div>
          <label className="block text-label text-content-muted mb-1.5 uppercase">Speed</label>
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => setPayoutMethod('standard')}
              className={`flex flex-col items-start gap-1 p-3 rounded-md border-2 text-left transition ${
                payoutMethod === 'standard' ? 'border-ink-900 bg-surface-sunken' : 'border-line'}`}>
              <Building size={16} className="text-ink-700" />
              <span className="text-body-s font-semibold text-ink-900">Standard</span>
              <span className="text-micro text-content-subtle">2-5 business days · no extra fee</span>
            </button>
            <button type="button" onClick={() => setPayoutMethod('instant')}
              className={`flex flex-col items-start gap-1 p-3 rounded-md border-2 text-left transition ${
                payoutMethod === 'instant' ? 'border-ink-900 bg-surface-sunken' : 'border-line'}`}>
              <Zap size={16} className="text-ink-700" />
              <span className="text-body-s font-semibold text-ink-900">Instant</span>
              <span className="text-micro text-content-subtle">~30 min · Stripe fee applies</span>
            </button>
          </div>
        </div>
      )}

      {/* Card-only account — no choice to make, just say what's happening. */}
      {connectStatus?.payoutsEnabled && connectStatus?.hasInstantCard && !connectStatus?.hasBankAccount && (
        <div className="flex items-center gap-3 p-3 rounded-md border-2 border-line">
          <Zap size={18} className="text-ink-700 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-body-m font-semibold text-ink-900">Instant to your debit card</p>
            <p className="text-micro text-content-subtle">No bank account on file — this is usually minutes, and Stripe's own fee applies on top of fetchr's above</p>
          </div>
        </div>
      )}

      {error && <AdvisoryBanner tone="error">{error}</AdvisoryBanner>}

      <div className="flex gap-2">
        <button onClick={onClose} className="btn-secondary flex-1">Cancel</button>
        <button onClick={handleWithdraw} disabled={loading || !connectStatus?.payoutsEnabled}
          className="btn-primary flex-[2] disabled:opacity-50">
          {loading
            ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Processing</>
            : <><ArrowUpCircle size={15} /> Withdraw ${net > 0 ? net.toFixed(2) : '0.00'}</>
          }
        </button>
      </div>
    </div>
  );
};

const TXN_ICON = {
  topup: ArrowDownCircle,
  withdrawal: Building,
  escrow_release: CheckCircle,
  escrow_hold: Lock,
  fetchr_fee: DollarSign,
};

// ── MAIN WALLET SCREEN ──
const WalletScreen = ({ session, forceWithdrawAll = false }) => {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [activePanel, setActivePanel] = useState(forceWithdrawAll ? 'withdraw' : null);
  const [success, setSuccess] = useState('');
  const [selectedTxn, setSelectedTxn] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const userId = session.user.id;
    // Explicit column list, not '*' — see Profile.jsx's fetchProfile for why.
    const { data: p } = await supabase.from('profiles')
      .select(PROFILE_SELF_COLUMNS)
      .eq('id', userId).single();
    if (p) setProfile(p);

    const { data: txns } = await supabase
      .from('transactions')
      .select(`*, match:matches(
        flight:flights(from_city, from_code, to_city, to_code, airline, flight_date),
        request:shipment_requests(item_name)
      )`)
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(50);
    if (txns) setTransactions(txns);
    setLoading(false);
  }, [session.user.id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getTxnIcon = (txn) => TXN_ICON[txn.type] || DollarSign;

  const isCredit = (txn) => ['topup', 'credit', 'escrow_release'].includes(txn.type);
  // "Total withdrawn" must only reflect money that actually left the wallet
  // balance. escrow_hold covers BOTH card-paid and wallet-paid escrow (same
  // type either way) — only the wallet-funded ones (escrow_from_wallet,
  // tagged metadata.payment_method: 'wallet') ever touched the wallet. A
  // card-paid escrow_hold never debited it, so it must not count here (a
  // partial wallet contribution already gets its own separate 'debit' row).
  const isWalletDebit = (txn) => {
    if (txn.type === 'withdrawal' || txn.type === 'debit') return true;
    if (txn.type === 'escrow_hold' && txn.metadata?.payment_method === 'wallet') return true;
    return false;
  };
  const totalCredits = transactions.filter(t => isCredit(t) && t.status === 'completed').reduce((s, t) => s + (t.amount || 0), 0);
  const totalDebits = transactions.filter(t => isWalletDebit(t) && t.status !== 'refunded').reduce((s, t) => s + (t.amount || 0), 0);

  if (loading) return (
    <div className="max-w-5xl mx-auto space-y-3">
      {[1, 2, 3].map(i => <RowSkeleton key={i} />)}
    </div>
  );

  return (
    <Elements stripe={stripePromise}>
      <div className="max-w-5xl mx-auto animate-fade-in">
        <div className="mb-6">
          <h1 className="font-display font-bold text-title-l text-ink-900">Wallet</h1>
          <p className="text-body-s text-content-muted mt-0.5">Manage your fetchr balance</p>
        </div>

        <Toast message={success} tone="success" />

        {/* Top up sheet */}
        {activePanel === 'topup' && (
          <BottomSheet title="Top up wallet" onClose={() => setActivePanel(null)}>
            <TopUpForm
              profile={profile}
              onSuccess={(amt) => {
                setActivePanel(null);
                setSuccess(`$${amt.toFixed(2)} added to your wallet.`);
                setTimeout(() => setSuccess(''), 5000);
                fetchData();
              }}
              onClose={() => setActivePanel(null)}
            />
          </BottomSheet>
        )}

        {/* Withdraw sheet */}
        {activePanel === 'withdraw' && (
          <BottomSheet title="Withdraw to bank account" onClose={() => setActivePanel(null)}>
            <WithdrawForm
              profile={profile}
              forceWithdrawAll={forceWithdrawAll}
              onSuccess={(result) => {
                setActivePanel(null);
                setSuccess(`Withdrawal of $${result.netAmount?.toFixed(2)} initiated. Arrives in ${result.estimatedArrival || '3-5 business days'}.`);
                setTimeout(() => setSuccess(''), 6000);
                fetchData();
              }}
              onClose={() => setActivePanel(null)}
            />
          </BottomSheet>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(320px,1fr)_1fr] gap-6 items-start">
          {/* Left column — balance, actions, stats */}
          <div className="space-y-4">
            <div className="bg-surface-inverse rounded-lg p-6 text-white">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-ink-300 text-body-s">Available balance</p>
                  <p className="font-mono font-bold text-display-m mt-1.5 text-white">
                    ${(profile?.wallet_balance || 0).toFixed(2)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-ink-800 rounded-md flex items-center justify-center flex-shrink-0">
                  <WalletCards size={22} className="text-white" />
                </div>
              </div>
              <p className="text-micro text-ink-300 flex items-center gap-1 mb-4">
                <Lock size={11} /> Secured by Stripe · funds held in a segregated account
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setActivePanel(activePanel === 'topup' ? null : 'topup')}
                  className="flex-1 flex items-center justify-center gap-2 min-h-[44px] px-4 rounded-md font-display font-semibold text-body-m bg-ink-800 text-white border border-ink-700 hover:bg-ink-700 transition">
                  <ArrowDownCircle size={16} /> Top up
                </button>
                <button
                  onClick={() => setActivePanel(activePanel === 'withdraw' ? null : 'withdraw')}
                  className="flex-1 flex items-center justify-center gap-2 min-h-[44px] px-4 rounded-md font-display font-semibold text-body-m bg-white text-ink-900 hover:bg-paper-100 transition">
                  <ArrowUpCircle size={16} /> Withdraw
                </button>
              </div>
            </div>

            {[
              { label: 'Total received', value: `$${totalCredits.toFixed(2)}`, icon: ArrowDownCircle, text: 'text-success', bg: 'bg-success-tint' },
              { label: 'Total withdrawn', value: `$${totalDebits.toFixed(2)}`, icon: ArrowUpCircle, text: 'text-danger', bg: 'bg-danger-tint' },
            ].map((s, i) => (
              <div key={i} className="card p-4 flex items-center gap-3">
                <div className={`w-9 h-9 ${s.bg} rounded-md flex items-center justify-center flex-shrink-0`}>
                  <s.icon size={16} className={s.text} />
                </div>
                <div>
                  <p className="text-micro text-content-subtle">{s.label}</p>
                  <p className="font-mono font-bold text-title-s text-ink-900">{s.value}</p>
                </div>
              </div>
            ))}
          </div>

        {/* Right column — transaction history */}
        <div className="card p-5">
          <h3 className="font-display font-bold text-title-s text-ink-900 mb-4">Transaction history</h3>
          {transactions.length === 0 ? (
            <EmptyState icon={WalletCards} title="No transactions yet" compact />
          ) : (
            <div className="space-y-1">
              {transactions.map((txn, i) => {
                const Icon = getTxnIcon(txn);
                return (
                  <button key={i} onClick={() => setSelectedTxn(txn)}
                    className="w-full flex items-center justify-between py-3 px-2 border-b border-line last:border-0 hover:bg-surface-sunken rounded-md transition text-left group">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0 ${
                        isCredit(txn) ? 'bg-success-tint' : 'bg-danger-tint'
                      }`}>
                        <Icon size={17} className={isCredit(txn) ? 'text-success' : 'text-danger'} />
                      </div>
                      <div>
                        <p className="text-body-m font-semibold text-ink-900 truncate max-w-[180px]">
                          {txn.description}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <p className="text-micro text-content-subtle">
                            {new Date(txn.created_at).toLocaleDateString('en-GB', {
                              day: '2-digit', month: 'short', year: 'numeric'
                            })}
                          </p>
                          <span className={txn.status === 'pending' ? 'badge-yellow' : 'badge-green'}>
                            {txn.status === 'pending' ? <><Clock size={9} /> Processing</> : <><CheckCircle size={9} /> Done</>}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <p className={`font-mono text-body-m font-bold ${isCredit(txn) ? 'text-success' : 'text-danger'}`}>
                        {isCredit(txn) ? '+' : '−'}${(txn.amount || 0).toFixed(2)}
                      </p>
                      <ChevronRight size={14} className="text-ink-300 group-hover:text-ink-500 transition" />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        </div>

        {/* Transaction detail sheet */}
        {selectedTxn && (
          <BottomSheet title="Transaction detail" onClose={() => setSelectedTxn(null)}
            footer={<button onClick={() => setSelectedTxn(null)} className="w-full btn-secondary">Close</button>}>
            <div className="p-6 space-y-4">
                <div className="text-center py-4">
                  <div className={`w-16 h-16 rounded-lg flex items-center justify-center mx-auto mb-3 ${
                    isCredit(selectedTxn) ? 'bg-success-tint' : 'bg-danger-tint'
                  }`}>
                    {(() => { const Icon = getTxnIcon(selectedTxn); return <Icon size={28} className={isCredit(selectedTxn) ? 'text-success' : 'text-danger'} />; })()}
                  </div>
                  <p className={`font-mono font-bold text-display-m ${isCredit(selectedTxn) ? 'text-success' : 'text-danger'}`}>
                    {isCredit(selectedTxn) ? '+' : '−'}${(selectedTxn.amount || 0).toFixed(2)}
                  </p>
                  <p className="text-content-muted text-body-s mt-1">{selectedTxn.description}</p>
                </div>
                <div className="bg-surface-sunken rounded-md p-4 space-y-2.5 text-body-s border border-line">
                  {[
                    { label: 'Date', value: new Date(selectedTxn.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }) },
                    { label: 'Time', value: new Date(selectedTxn.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) },
                    { label: 'Type', value: selectedTxn.type === 'topup' ? 'Wallet top up' : selectedTxn.type === 'withdrawal' ? 'Bank withdrawal' : selectedTxn.type === 'escrow_release' ? 'Delivery payment' : selectedTxn.type === 'escrow_hold' ? 'Escrow payment' : selectedTxn.type === 'fetchr_fee' ? 'fetchr fee' : selectedTxn.type },
                    { label: 'Status', value: selectedTxn.status === 'pending' ? 'Processing' : 'Completed', color: selectedTxn.status === 'pending' ? 'text-warning' : 'text-success' },
                  ].map((row, i) => (
                    <div key={i} className="flex justify-between">
                      <span className="text-content-subtle">{row.label}</span>
                      <span className={`font-semibold ${row.color || 'text-ink-900'}`}>{row.value}</span>
                    </div>
                  ))}
                  {selectedTxn.type === 'withdrawal' && selectedTxn.metadata?.fee != null && (
                    <div className="border-t border-line pt-2 space-y-1 font-mono">
                      <div className="flex justify-between">
                        <span className="text-content-subtle">Gross amount</span>
                        <span className="font-semibold text-ink-900">${selectedTxn.amount.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-content-subtle">Fee (2.5%)</span>
                        <span className="font-semibold text-danger">−${selectedTxn.metadata.fee.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between font-bold text-ink-900">
                        <span>You received</span>
                        <span>${selectedTxn.metadata.net.toFixed(2)}</span>
                      </div>
                    </div>
                  )}
                </div>
                {selectedTxn.match && (
                  <div className="bg-surface-sunken rounded-md p-4 border border-line space-y-2 text-body-s">
                    <p className="text-label text-ink-600 mb-2">Deal details</p>
                    {[
                      { label: 'Route', value: `${selectedTxn.match.flight?.from_code} → ${selectedTxn.match.flight?.to_code}` },
                      { label: 'Item', value: selectedTxn.match.request?.item_name },
                      selectedTxn.match.flight?.airline && { label: 'Airline', value: selectedTxn.match.flight.airline },
                      selectedTxn.match.flight?.flight_date && { label: 'Flight date', value: new Date(selectedTxn.match.flight.flight_date).toLocaleDateString('en-GB') },
                    ].filter(Boolean).map((row, i) => (
                      <div key={i} className="flex justify-between">
                        <span className="text-content-subtle">{row.label}</span>
                        <span className="font-semibold text-ink-900">{row.value}</span>
                      </div>
                    ))}
                  </div>
                )}
            </div>
          </BottomSheet>
        )}
      </div>
    </Elements>
  );
};

export default WalletScreen;
